import { useState, useCallback, useMemo, useRef } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'

type SortableItem = {
  id: number | null
  sort_order?: number | null
}

type DiffItem = { id: number; sort_order: number }

export const useSortOrder = <T extends SortableItem>(
  initialItems: T[],
  getKey: (item: T) => string,
) => {
  const [isSortMode, setIsSortMode] = useState(false)
  const [sortedItems, setSortedItems] = useState<T[]>([])

  // Store latest getKey in a ref so handlers don't recreate when caller passes inline arrow fn
  const getKeyRef = useRef(getKey)
  getKeyRef.current = getKey

  const stableItems = useMemo(() => {
    return [...initialItems].sort((a, b) => {
      const aOrder = a.sort_order ?? Infinity
      const bOrder = b.sort_order ?? Infinity
      return aOrder !== bOrder ? aOrder - bOrder : (a.id ?? 0) - (b.id ?? 0)
    })
  }, [initialItems])

  const unsavedItems = useMemo(
    () => stableItems.filter((item) => item.id === null),
    [stableItems],
  )

  const enterSortMode = useCallback(() => {
    setSortedItems(stableItems)
    setIsSortMode(true)
  }, [stableItems])

  const cancelSortMode = useCallback(() => {
    setSortedItems([])
    setIsSortMode(false)
  }, [])

  const isDirty = useMemo(() => {
    if (!isSortMode || sortedItems.length === 0) return false
    return sortedItems.some((item, index) => item.id !== stableItems[index]?.id)
  }, [sortedItems, stableItems, isSortMode])

  // Returns { id, sort_order }[] — caller maps id → entity-specific field name (room_id / rate_id)
  const computeDiff = useCallback((): DiffItem[] => {
    return sortedItems
      .filter((item) => item.id !== null)
      .map((item, index) => ({ id: item.id as number, sort_order: index + 1 }))
      .filter((diff) => {
        const orig = initialItems.find((r) => r.id === diff.id)
        return (orig?.sort_order ?? null) !== diff.sort_order
      })
  }, [sortedItems, initialItems])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setSortedItems((prev) => {
      const oldIndex = prev.findIndex((item) => getKeyRef.current(item) === String(active.id))
      const newIndex = prev.findIndex((item) => getKeyRef.current(item) === String(over.id))
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [])

  const handleMoveUp = useCallback((key: string) => {
    setSortedItems((prev) => {
      const index = prev.findIndex((item) => getKeyRef.current(item) === key)
      if (index <= 0) return prev
      return arrayMove(prev, index, index - 1)
    })
  }, [])

  const handleMoveDown = useCallback((key: string) => {
    setSortedItems((prev) => {
      const index = prev.findIndex((item) => getKeyRef.current(item) === key)
      if (index < 0 || index >= prev.length - 1) return prev
      return arrayMove(prev, index, index + 1)
    })
  }, [])

  const displayItems = isSortMode ? sortedItems : stableItems

  return {
    isSortMode,
    isDirty,
    displayItems,
    unsavedItems,
    computeDiff,
    enterSortMode,
    cancelSortMode,
    handleDragEnd,
    handleMoveUp,
    handleMoveDown,
  }
}
